import type { z } from 'zod';

import { AccountRefSchema, type AccountRef } from '../../users/account-ref/account-ref';

import { SignalMatchSchema } from '../signal-match/signal-match';

/**
 * One side of a recorded collision, as the review queue names it.
 *
 * **An alias, not a copy** (P15a). This shape was first written here for the
 * Trust queue, and by P15 three surfaces needed it — the verification queue
 * names the member whose evidence is being judged in exactly the same four
 * fields. It now lives in `users` as {@link AccountRefSchema}, beside the
 * `UserStatus` it carries and the batched `USERS_GET_ACCOUNT_REFS` query that
 * resolves it, and the argument for its four fields (and for the `bannedAt` that
 * is deliberately absent) is written there.
 *
 * The alias is kept rather than the call sites rewritten: `TrustAccountRef` is
 * what `modules/Trust` calls this thing, and renaming a module's vocabulary is
 * not what promoting a shape is for.
 */
export const TrustAccountRefSchema = AccountRefSchema;

export type TrustAccountRef = AccountRef;

/**
 * A queue row: the match, plus both accounts resolved (`03-trust-ban-evasion.md`
 * §admin Trust module — "both accounts side-by-side").
 *
 * **Both sides are nullable, for two different reasons, and both are real
 * states the surface renders:**
 * - `subject` is null when the pipeline **blocked** a registration — the account
 *   was refused before it was written, so there is nothing to point at and
 *   nothing to ban. `confirm` answers `no_subject_to_ban` on such a row, and the
 *   queue offers `dismiss` instead of a button the api will refuse.
 * - `matched` is null when the banned account can no longer be looked up. The
 *   column is `notNull`, so this is not the ordinary case; it is the honest
 *   answer to a lookup that came back short, and it keeps one missing row from
 *   throwing inside a mapper and blanking the whole page.
 *
 * The account lookup crosses a module boundary (`access.users` belongs to
 * `users`), so it goes over the QueryBus (N6) — this contract is the shape that
 * comes back out of that, never a joined row.
 */
export const SignalMatchDetailSchema = SignalMatchSchema.extend({
  subject: TrustAccountRefSchema.nullable(),
  matched: TrustAccountRefSchema.nullable(),
}).strict();

export type SignalMatchDetail = z.infer<typeof SignalMatchDetailSchema>;
